import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { perfHubPublicCatalogPrices } from '../api/api';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import './Pricing.css';

function formatInr(n) {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(
    Math.round(n)
  );
}

/**
 * Performance Hub catalog on this page: amounts come from
 * ``GET /api/performance-hub/public/catalog-prices`` (backend ``PERF_HUB_*_SUBSCRIPTION_INR``).
 */
const PH_LAUNCH_OFF_FRACTION = 0.6;

function phListPriceInr(saleInr) {
  const payFraction = 1 - PH_LAUNCH_OFF_FRACTION;
  if (payFraction <= 0) return saleInr;
  return Math.round(saleInr / payFraction);
}

const PH_DEFAULT_SALE_INR = { pro: 1000, enterprise: 2500 };

function buildPhPlansForPricing(checkoutPricesInr) {
  const m = checkoutPricesInr && typeof checkoutPricesInr === 'object' ? checkoutPricesInr : {};
  const proSale =
    m.pro != null && Number.isFinite(Number(m.pro)) ? Number(m.pro) : PH_DEFAULT_SALE_INR.pro;
  const entSale =
    m.enterprise != null && Number.isFinite(Number(m.enterprise))
      ? Number(m.enterprise)
      : PH_DEFAULT_SALE_INR.enterprise;
  return [
    {
      key: 'free',
      name: 'Free',
      tagline: 'For small teams testing Performance Hub',
      saleInr: 0,
      listInr: 0,
      maxCampaigns: 1,
      maxCreators: 5,
      cta: 'Start free',
      ctaTo: '/signup',
      features: [
        'CSV import (handle, email, post URL)',
        'Magic-link Instagram connect for creators',
        'In-app metric dashboards & top performers',
        'Daily auto-sync + manual refresh',
        'Private CRM shortlist',
      ],
      limitations: ['CSV export not included', 'No priority support'],
    },
    {
      key: 'pro',
      name: 'Pro',
      tagline: 'For growing brands with active creator rosters',
      saleInr: proSale,
      listInr: phListPriceInr(proSale),
      maxCampaigns: 5,
      maxCreators: 250,
      cta: 'Upgrade to Pro',
      ctaTo: '/performance?tab=plans',
      features: [
        'Everything in Free, plus:',
        'Up to 5 active tracking campaigns',
        'Up to 250 connected creators',
        'CSV export of metrics and rows',
        'Higher invite resend limits',
        'Email support',
      ],
      highlighted: true,
    },
    {
      key: 'enterprise',
      name: 'Enterprise',
      tagline: 'For agencies and large rosters',
      saleInr: entSale,
      listInr: phListPriceInr(entSale),
      maxCampaigns: 15,
      maxCreators: 750,
      cta: 'Upgrade to Enterprise',
      ctaTo: '/performance?tab=plans',
      features: [
        'Everything in Pro, plus:',
        'Up to 15 active tracking campaigns',
        'Up to 750 connected creators',
        'Priority email support',
        'Direct line for billing / data requests',
      ],
    },
  ];
}

function Pricing() {
  const navigate = useNavigate();
  const [phPlans, setPhPlans] = useState(() => buildPhPlansForPricing(null));
  const [phCheckoutEnabled, setPhCheckoutEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    perfHubPublicCatalogPrices()
      .then((res) => {
        if (cancelled) return;
        setPhPlans(buildPhPlansForPricing(res.data?.checkout_prices_inr));
        setPhCheckoutEnabled(res.data?.checkout_enabled !== false);
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  return (
    <div className="pricing-page">
      <Navbar />
      
      <section className="pricing-hero">
        <span className="hero-badge">TRANSPARENT PRICING</span>
        {MILAN_WORKSPACE_ENABLED ? (
          <>
            <h1>Simple, Fair Pricing for Influencer Marketing</h1>
            <p>
              No monthly subscriptions or hidden charges. 
              Access all features free, pay only a small success fee when campaigns are completed.
            </p>
          </>
        ) : (
          <>
            <h1>Simple pricing for Instagram performance tracking</h1>
            <p>
              Free tier to start. Flat monthly plans when your roster grows — no escrow, no campaign success fee.
            </p>
          </>
        )}
      </section>

      {MILAN_WORKSPACE_ENABLED && (
      <section className="pricing-cards">
        <div className="pricing-card free-card">
          <span className="card-badge free-badge">ALWAYS FREE</span>
          <h2>Full Platform Access</h2>
          <p className="card-subtitle">Every feature unlocked. Zero restrictions. No surprises.</p>
          
          <div className="price-display">
            <span className="currency">₹</span>
            <span className="amount">0</span>
          </div>
          <p className="price-period">Forever free, no trials</p>
          
          <ul className="feature-list">
            <li><span className="check">✓</span> Create unlimited campaigns</li>
            <li><span className="check">✓</span> Full campaign lifecycle management</li>
            <li><span className="check">✓</span> Real-time performance analytics</li>
            <li><span className="check">✓</span> Advanced creator discovery & filters</li>
            <li><span className="check">✓</span> Unlimited direct messaging</li>
            <li><span className="check">✓</span> Content review & approval system</li>
            <li><span className="check">✓</span> Smart negotiation tools</li>
            <li><span className="check">✓</span> Delivery & shipment tracking</li>
            <li><span className="check">✓</span> AI-powered creator matching</li>
            <li><span className="check">✓</span> Detailed reporting dashboard</li>
          </ul>

          <div className="savings-box">
            <span className="savings-icon">💰</span>
            <div>
              <p className="savings-title">Pay Only for Success</p>
              <p className="savings-compare">Upfront cost: <span>₹0</span></p>
              <p className="savings-result">You pay: <strong>3.5% only on completed campaigns</strong></p>
            </div>
          </div>

          <button className="cta-button free-cta" onClick={handleGetStarted}>
            Get Started Free - Zero Card Required
          </button>
        </div>

        <div className="pricing-card paid-card">
          <span className="card-badge paid-badge">SUCCESS-BASED FEE</span>
          <h2>Platform Fee</h2>
          <p className="card-subtitle">Only charged when campaign is successfully completed</p>
          
          <div className="price-display paid-price">
            <span className="amount">3.5%</span>
          </div>
          <p className="price-period">Of total campaign value</p>

          <div className="fee-info-box">
            <h4>📋 How It Works</h4>
            <p>Platform fee structure:</p>
            <ul>
              <li>• 3.5% charged to brands upfront (added to escrow)</li>
              <li>• 3.5% deducted from creator payments</li>
              <li>• Platform fee is non-refundable once campaign starts</li>
            </ul>
          </div>

          <div className="payment-methods">
            <h4>💰 Payment Structure</h4>
            
            <div className="method-option recommended">
              <div className="method-info">
                <span className="method-name">Fixed Payment</span>
                <span className="method-badge">60%</span>
              </div>
              <div className="method-details">
                <span className="method-fee">Weekly</span>
                <span className="method-time">During monitoring</span>
              </div>
              <span className="method-breakdown">Released weekly over the monitoring period</span>
            </div>

            <div className="method-option">
              <div className="method-info">
                <span className="method-name">Performance Payment</span>
                <span className="method-badge">40%</span>
              </div>
              <div className="method-details">
                <span className="method-fee">End</span>
                <span className="method-time">After monitoring</span>
              </div>
              <span className="method-breakdown">Based on actual engagement metrics</span>
            </div>
          </div>

          <div className="savings-tip">
            <span>💡</span> Monitoring period: 1–4 weeks or 1–30 days (you choose)
          </div>
        </div>
      </section>
      )}

      <section className="ph-plans-section" aria-labelledby="ph-plans-heading" id="estimator-heading">
        <div className="ph-plans-inner">
          <span className="ph-plans-eyebrow">
            PERFORMANCE HUB
            <span className="ph-plans-live-pill" aria-label="Live feature">LIVE</span>
          </span>
          <h2 id="ph-plans-heading">Performance Hub plans</h2>
          <p className="ph-plans-lead">
            {MILAN_WORKSPACE_ENABLED
              ? 'Track Instagram performance for creator lists you already own — separate from Milan campaigns and escrow. Free for small teams; flat monthly subscription for larger rosters.'
              : 'Track Instagram performance for creator lists you already own. Free for small teams; flat monthly subscription for larger rosters. No escrow — you keep payments in your own tools.'}
          </p>
          <p className="ph-plans-launch-strip">
            <span aria-hidden="true">🎉</span> Launch offer: <strong>60% off</strong> Pro &amp; Enterprise for a
            limited time.
          </p>
          {!phCheckoutEnabled && (
            <p className="ph-plans-checkout-paused" role="status">
              Self-serve paid checkout is temporarily unavailable; Performance Hub Free still works in the app.
            </p>
          )}

          <div className="ph-plans-grid">
            {phPlans.map((plan) => {
              const isPaid = plan.saleInr > 0;
              return (
                <div
                  key={plan.key}
                  className={`ph-plan-card ${plan.highlighted ? 'highlighted' : ''} plan-${plan.key}`}
                >
                  {plan.highlighted && <span className="ph-plan-ribbon">Most popular</span>}
                  <div className="ph-plan-head">
                    <h3 className="ph-plan-name">{plan.name}</h3>
                    <p className="ph-plan-tagline">{plan.tagline}</p>
                  </div>

                  <div className="ph-plan-price">
                    {isPaid ? (
                      <>
                        <span className="ph-plan-amount">
                          <span className="ph-plan-currency">₹</span>
                          {formatInr(plan.saleInr)}
                        </span>
                        <span className="ph-plan-period">/ month</span>
                        {plan.listInr > plan.saleInr && (
                          <div className="ph-plan-listprice">
                            <span className="ph-plan-strike">₹{formatInr(plan.listInr)}</span>
                            <span className="ph-plan-discount">
                              {Math.round(PH_LAUNCH_OFF_FRACTION * 100)}% off
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="ph-plan-amount">
                          <span className="ph-plan-currency">₹</span>0
                        </span>
                        <span className="ph-plan-period">forever</span>
                      </>
                    )}
                  </div>

                  <ul className="ph-plan-limits">
                    <li>
                      <strong>{plan.maxCampaigns}</strong> active tracking{' '}
                      {plan.maxCampaigns === 1 ? 'campaign' : 'campaigns'}
                    </li>
                    <li>
                      <strong>{plan.maxCreators}</strong> connected creators
                    </li>
                  </ul>

                  <ul className="ph-plan-features">
                    {plan.features.map((f, i) => (
                      <li key={i}>
                        <span className="ph-plan-check">✓</span>
                        {f}
                      </li>
                    ))}
                    {(plan.limitations || []).map((f, i) => (
                      <li key={`x-${i}`} className="ph-plan-limit-row">
                        <span className="ph-plan-x">×</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {isPaid && !phCheckoutEnabled ? (
                    <span
                      className={`ph-plan-cta ph-plan-cta--disabled ${plan.highlighted ? 'primary' : 'ghost'}`}
                      aria-disabled="true"
                    >
                      Checkout temporarily unavailable
                    </span>
                  ) : (
                    <Link
                      to={plan.ctaTo}
                      className={`ph-plan-cta ${plan.highlighted ? 'primary' : 'ghost'}`}
                    >
                      {plan.cta}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <p className="ph-plans-finer">
            Pricing and caps are enforced at the workspace level and match the in-app{' '}
            <Link to="/performance?tab=plans">Plans &amp; Pricing</Link> page. Prices are in INR and exclude
            applicable taxes and Razorpay processing fees. Subscriptions do not auto-renew; see{' '}
            <Link to="/terms">Terms §6.16</Link> for refunds and acceptable use.
            {MILAN_WORKSPACE_ENABLED && (
              <>
                {' '}Performance Hub is{' '}
                <strong>separate</strong> from the 3.5% Milan campaign success fee shown above.
              </>
            )}
          </p>
        </div>
      </section>

      {MILAN_WORKSPACE_ENABLED && (
      <>
      <section className="comparison-section">
        <h2>What You Get</h2>
        <p>Full platform access with transparent pricing</p>

        <div className="comparison-table">
          <div className="table-header">
            <div className="col-feature">Feature</div>
            <div className="col-others">Common Alternatives</div>
            <div className="col-milan">Milan AI</div>
          </div>

          <div className="table-row">
            <div className="col-feature">Platform Access</div>
            <div className="col-others negative">Monthly subscription</div>
            <div className="col-milan positive">Free</div>
          </div>

          <div className="table-row">
            <div className="col-feature">Setup & Onboarding</div>
            <div className="col-others negative">Often charged separately</div>
            <div className="col-milan positive">Free</div>
          </div>

          <div className="table-row">
            <div className="col-feature">Campaign Limits</div>
            <div className="col-others negative">Often tier-based</div>
            <div className="col-milan positive">Unlimited</div>
          </div>

          <div className="table-row">
            <div className="col-feature">AI Creator Matching</div>
            <div className="col-others negative">Sometimes extra cost</div>
            <div className="col-milan positive">Included</div>
          </div>

          <div className="table-row">
            <div className="col-feature">Performance Analytics</div>
            <div className="col-others negative">Varies by plan</div>
            <div className="col-milan positive">Always included</div>
          </div>

          <div className="table-row">
            <div className="col-feature">Performance Hub (your own IG lists)</div>
            <div className="col-others negative">Often a separate analytics tool</div>
            <div className="col-milan positive">
              Free, Pro &amp; Enterprise plans (see plans above)
            </div>
          </div>

          <div className="table-row">
            <div className="col-feature">Escrow Payments</div>
            <div className="col-others negative">Not always available</div>
            <div className="col-milan positive">Built-in</div>
          </div>

          <div className="table-row highlight-row">
            <div className="col-feature">Success Fee</div>
            <div className="col-others negative">15-30% agency fees</div>
            <div className="col-milan positive">Only 3.5%</div>
          </div>
        </div>
      </section>

      <section className="breakdown-section">
        <h2>Crystal Clear Pricing</h2>
        <p>No hidden charges. No gotchas. Here's exactly what's free and what's not.</p>

        <div className="breakdown-cards">
          <div className="breakdown-card">
            <span className="breakdown-badge free">100% FREE</span>
            <h3>Platform Features</h3>
            <ul>
              <li>✓ Instagram creator discovery</li>
              <li>✓ Campaign creation & management</li>
              <li>✓ Campaign invitation system</li>
              <li>✓ Content submission & approval</li>
              <li>✓ Performance tracking</li>
              <li>✓ AI-powered recommendations</li>
              <li>✓ CRM for saved creators</li>
              <li>✓ Dashboard analytics</li>
            </ul>
          </div>

          <div className="breakdown-card">
            <span className="breakdown-badge paid">SUCCESS FEE</span>
            <h3>3.5% Platform Fee</h3>
            <ul>
              <li>→ Added on top of your campaign budget at checkout</li>
              <li>→ Creators receive their full quoted amount</li>
              <li>→ Non-refundable once campaign starts</li>
              <li>→ Includes escrow protection & performance tracking</li>
            </ul>
            <p className="breakdown-note">
              <strong>Note:</strong> The platform fee covers secure payments,
              performance monitoring, and dispute resolution.
            </p>
          </div>
        </div>
      </section>

      <section className="refund-policy-section">
        <h2>Cancellation & Refund Policy</h2>
        <p>Transparent policies to protect both brands and creators</p>

        <div className="refund-cards">
          <div className="refund-card">
            <div className="refund-icon">💳</div>
            <h3>Payment Gateway Fee</h3>
            <p>
              When you make a payment, our payment partner (Razorpay) charges a processing fee of approximately 
              <strong> 2.36%</strong> (2% + 18% GST). This is separate from the platform fee.
            </p>
          </div>

          <div className="refund-card highlight">
            <div className="refund-icon">🔒</div>
            <h3>Platform Fee (Non-Refundable)</h3>
            <p>
              The <strong>3.5% platform fee</strong> is charged upfront and is <strong>non-refundable</strong> once 
              the campaign starts. This covers escrow management, performance tracking, and secure payments.
            </p>
          </div>
        </div>

        <div className="refund-table-container">
          <h3>📋 Refund Schedule</h3>
          <table className="refund-table">
            <thead>
              <tr>
                <th>Cancellation Stage</th>
                <th>Refund Amount</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className="stage-badge green">Draft (no payment made)</span>
                </td>
                <td><strong>N/A</strong></td>
                <td>No payment, no refund needed</td>
              </tr>
              <tr>
                <td>
                  <span className="stage-badge green">Before creator accepts</span>
                </td>
                <td><strong>Full creator allocation</strong></td>
                <td>Platform fee (3.5%) is non-refundable</td>
              </tr>
              <tr>
                <td>
                  <span className="stage-badge yellow">After acceptance, before work</span>
                </td>
                <td><strong>Full creator allocation</strong></td>
                <td>Platform fee (3.5%) is non-refundable</td>
              </tr>
              <tr>
                <td>
                  <span className="stage-badge orange">Content submitted (max revisions)</span>
                </td>
                <td><strong>Allocation minus 8.5% kill fee</strong></td>
                <td>5% to creator + 3.5% platform cancellation fee (of fixed payment)</td>
              </tr>
              <tr>
                <td>
                  <span className="stage-badge red">After content approved</span>
                </td>
                <td><strong>Cannot cancel</strong></td>
                <td>Campaign must proceed to completion</td>
              </tr>
              <tr>
                <td>
                  <span className="stage-badge blue">Unused performance budget</span>
                </td>
                <td><strong>100%</strong> of unused amount</td>
                <td>Full refund for unused performance allocation</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="refund-example">
          <h4>💡 Example</h4>
          <p>
            If your creator allocation totals <strong>₹10,000</strong>, you'll be charged <strong>₹10,350</strong> at checkout
            (₹10,000 to creators + ₹350 platform fee on top):
          </p>
          <ul>
            <li>Cancel before acceptance: ₹10,000 refunded (platform fee ₹350 non-refundable)</li>
            <li>Cancel after work starts: Refund depends on work completed + kill fees</li>
            <li>Unused performance budget: 100% refunded to you</li>
          </ul>
          <p className="example-note">
            The 3.5% platform fee covers secure escrow, performance monitoring, and dispute resolution -
            ensuring a safe experience for both brands and creators.
          </p>
        </div>

        <div className="refund-note">
          <span>📌</span>
          <p>
            <strong>Kill Fee Protection:</strong> If a campaign is cancelled after a creator has submitted
            content (after maximum revisions), 8.5% of their fixed payment is deducted from the brand's
            escrow — <strong>5% goes to the creator</strong> as a kill fee, and 3.5% is retained as a
            platform cancellation fee. This protects creators for work done.
          </p>
        </div>
      </section>
      </>
      )}

      <section className="cta-section">
        <div className="cta-content">
          {MILAN_WORKSPACE_ENABLED ? (
            <>
              <h2>Ready to Start Your Campaign?</h2>
              <p>Create your account and launch your first campaign today</p>
              <button className="cta-button primary-cta" onClick={handleGetStarted}>
                Get Started Free
              </button>
              <span className="cta-note">No credit card required • No upfront fees • Pay only on success</span>
            </>
          ) : (
            <>
              <h2>Ready to start tracking?</h2>
              <p>Create a brand account and open Performance Hub — free tier available</p>
              <button className="cta-button primary-cta" onClick={handleGetStarted}>
                Get Started Free
              </button>
              <span className="cta-note">No credit card required • Free tier • Upgrade anytime</span>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Pricing;
